// Monitoring: driver requests, trips (route runs), collections and order
// approvals. All of it is read from the single pickup-order array.

import { DEMO_RECYCLER_ORG_ID, demoError, parseQueryParam, type DemoCtx, type DemoRouteDef } from '../core';
import { ensureDemoData } from '../data';
import { materialName } from '../data/catalog';
import { bodyOf } from '../data/common';
import { addNotification } from '../data/notifications';
import { approveOrder, orderById, rejectOrder, runById } from '../data/orders';
import { collectionRows, fleetAssignments, pendingApprovalRows, runsInRange, toApprovalRow, toRouteRun } from '../data/views';

function q(ctx: Pick<DemoCtx, 'query'>, key: string): string | undefined {
  return parseQueryParam(ctx, key);
}

function orgOf(ctx: DemoCtx): string {
  return q(ctx, 'organization_id') ?? ctx.orgId ?? DEMO_RECYCLER_ORG_ID;
}

export const monitoringRoutes: DemoRouteDef[] = [
  ['get', '/pickup_order/fleet/:orgId/assignments', ({ params, query }) => {
    ensureDemoData();
    return { result: fleetAssignments(params.orgId, parseQueryParam({ query }, 'status'), query.page, query.limit) };
  }],
  ['get', '/pickup_order/ops/collections', (ctx) => {
    ensureDemoData();
    return { result: { orders: collectionRows(orgOf(ctx), q(ctx, 'run_date')) } };
  }],
  ['get', '/pickup_order/ops/pending-approval', (ctx) => {
    ensureDemoData();
    return { result: { orders: pendingApprovalRows(orgOf(ctx), q(ctx, 'approval_status') ?? 'pending', q(ctx, 'run_date')) } };
  }],
  ['post', '/pickup_order/ops/:orderId/approve', ({ params, body }) => {
    ensureDemoData();
    const corrected = bodyOf(body).corrected_weight_kg;
    const res = approveOrder(params.orderId, corrected == null ? undefined : Number(corrected));
    if (!res) return demoError(404, 'Pickup order not found');
    if (res === 'not_pending') return demoError(409, 'This order has already been decided');
    return { result: toApprovalRow(res) };
  }],
  ['post', '/pickup_order/ops/:orderId/reject', ({ params, body }) => {
    ensureDemoData();
    const reason = String(bodyOf(body).reason ?? '').trim();
    if (reason.length < 3) return demoError(422, 'A rejection reason is required');
    const res = rejectOrder(params.orderId, reason);
    if (!res) return demoError(404, 'Pickup order not found');
    if (res === 'not_pending') return demoError(409, 'This order has already been decided');
    // The delivering organization is told why.
    if (res.outlet_org_id) {
      addNotification(
        res.outlet_org_id,
        ['Load rejected', 'تم رفض الحمولة'],
        [`${res.reference} (${materialName(res.material_id)}) was rejected: ${reason}`, `تم رفض ${res.reference} (${materialName(res.material_id)}): ${reason}`],
        { pickup_order_id: res.id },
      );
    }
    return { result: toApprovalRow(res) };
  }],
  ['post', '/pickup_order/admin/:orderId/reinstate-decision', ({ params }) => {
    ensureDemoData();
    if (!orderById(params.orderId)) return demoError(404, 'Pickup order not found');
    return demoError(403, 'Only platform administrators can reinstate a decision');
  }],
  ['get', '/ops/route-runs', (ctx) => {
    ensureDemoData();
    return { result: { runs: runsInRange(orgOf(ctx), q(ctx, 'date_from'), q(ctx, 'date_to')) } };
  }],
  ['get', '/ops/route-runs/:runId', ({ params }) => {
    ensureDemoData();
    const run = runById(params.runId);
    return run ? { result: toRouteRun(run) } : demoError(404, 'Trip not found');
  }],
];
